import { collections, movies, type CategoryKey, type Movie } from '@/mocks/movies';

export type { CategoryKey, Movie };
export { collections, movies };

export interface CategoryMeta {
  key: CategoryKey;
  name: string;
  h1: string;
  description: string;
  slug: string;
}

const categoryMap: Record<CategoryKey, CategoryMeta> = {
  latest: {
    key: 'latest',
    name: '最新',
    h1: '最新 AV 影片',
    slug: 'latest',
    description:
      '按更新时间排序的最新上架影片合集，覆盖都市、文艺、悬疑与治愈题材，每天持续补充新的高清片源。',
  },
  hot: {
    key: 'hot',
    name: '热门',
    h1: '热门 AV 影片',
    slug: 'hot',
    description:
      '根据站内播放量与收藏数据汇总的人气榜单，收录近期讨论度最高的作品，适合作为第一次浏览的起点。',
  },
  classic: {
    key: 'classic',
    name: '经典',
    h1: '经典 AV 影片',
    slug: 'classic',
    description:
      '经过修复与重新整理的经典作品，从一九八〇年代到二〇一九年，保留了胶片质感与那个年代独有的叙事节奏。',
  },
};

const aliasMap: Record<string, CategoryKey> = {
  latest: 'latest',
  hot: 'hot',
  classic: 'classic',
  最新: 'latest',
  热门: 'hot',
  经典: 'classic',
};

export function resolveCategory(type?: string): CategoryMeta | null {
  if (!type) return null;
  const key = aliasMap[type];
  return key ? categoryMap[key] : null;
}

export function getCategoryMeta(key: CategoryKey): CategoryMeta {
  return categoryMap[key];
}

export const categoryList: CategoryMeta[] = [categoryMap.latest, categoryMap.hot, categoryMap.classic];

export function getMovieById(id?: string): Movie | null {
  if (!id) return null;
  return movies.find((movie) => movie.id === id) ?? null;
}

export function getMoviesByCategory(type: CategoryKey): Movie[] {
  if (type === 'latest') {
    return [...movies].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }
  if (type === 'hot') {
    return movies.filter((movie) => movie.categories.includes('hot')).sort((a, b) => b.views - a.views);
  }
  return movies.filter((movie) => movie.categories.includes('classic')).sort((a, b) => b.rating - a.rating);
}

export function getFeaturedMovies(): Movie[] {
  const featured = ['night-neon', 'midnight-train', 'ginza-lights'];
  return featured
    .map((id) => movies.find((movie) => movie.id === id))
    .filter((movie): movie is Movie => Boolean(movie));
}

export function getLatestMovies(limit = 8): Movie[] {
  return getMoviesByCategory('latest').slice(0, limit);
}

export function getHotRanking(limit = 5): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function searchMovies(query: string): Movie[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.latin,
      movie.region,
      movie.director,
      ...movie.actors,
      ...movie.tags,
      movie.synopsis,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(keyword);
  });
}

export function getRelatedMovies(movie: Movie, limit = 5): Movie[] {
  const sameRegion = movies.filter(
    (item) => item.id !== movie.id && item.region === movie.region,
  );
  const sharedTag = movies.filter(
    (item) => item.id !== movie.id && item.tags.some((tag) => movie.tags.includes(tag)),
  );
  const pool = [...sameRegion, ...sharedTag, ...movies.filter((item) => item.id !== movie.id)];
  const unique: Movie[] = [];
  pool.forEach((item) => {
    if (!unique.some((exists) => exists.id === item.id)) unique.push(item);
  });
  return unique.slice(0, limit);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return views.toLocaleString('zh-CN');
}

export function formatDate(date: string): string {
  return date.replace(/-/g, '.');
}

export function formatDuration(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hour === 0) return `${rest} 分钟`;
  return `${hour} 小时 ${rest} 分`;
}

export const regions = ['全部', '日本', '韩国', '中国台湾', '欧美'];
export const years = ['全部', '2026', '2025', '2024', '2023', '更早'];